<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import trim from "lodash-es/trim";

import { toast } from "@/common/utils/toast";
import { getDate, getHour } from "@/common/utils/dates";

import {
  accessoryRelatedList,
  accessoryRelatedListId,
  accessoryRelatedCreate,
  accessoryRelatedDelete,
} from "@/common/api/rest/accessory/related";

import { accessoryList } from "@/common/api/rest/accessory/accessory";

import type { AccessoryRelatedType } from "@/common/types/accessory/related";
import type { AccessoryType } from "@/common/types/accessory/accessory";

import {
  baseAvatar,
  baseConfirm,
  baseLoading,
  baseInput,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  accessoryId: string;
}>();

const related = ref<AccessoryRelatedType[]>([]);
const relatedIds = ref<{ item_id: string; accessory_id: string }[]>([]);
const selected = ref<AccessoryRelatedType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(true);
const showModalAddRelated = ref(false);
const showModalRemoveRelated = ref(false);

const accessorySearchListRef = ref<any>(null);
const accessorySearch = ref<string>("");
const accessorySearchList = ref<AccessoryType[]>([]);
const accessoryLimit = ref<number>(10);
const accessoryPage = ref<number>(0);
const accessoryPages = ref<number>(0);
const accessorySearched = ref<number>(0);
const accessoryTotal = ref<number>(0);
const loadingMoreAccessories = ref(false);

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "created", label: t("common.labels.created") },
]);

async function fetchRelated() {
  await accessoryRelatedList(props.accessoryId, {
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: "DESC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        related.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchRelatedId() {
  await accessoryRelatedListId(props.accessoryId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        relatedIds.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function searchAccessories(append = false) {
  if (
    append &&
    (loadingMoreAccessories.value ||
      accessoryPage.value + 1 >= accessoryPages.value)
  ) {
    return;
  }

  if (append) {
    loadingMoreAccessories.value = true;
  }

  const pageToFetch = append ? accessoryPage.value + 1 : 0;

  await accessoryList({
    page: pageToFetch,
    limit: accessoryLimit.value,
    search: accessorySearch.value,
    sort: "ASC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newItems = (data.list ?? []).filter(
          (item: AccessoryType) => item.accessory_id !== props.accessoryId,
        );
        accessorySearchList.value = append
          ? [...accessorySearchList.value, ...newItems]
          : newItems;
        accessoryPage.value = pageToFetch;
        accessoryPages.value = data.infos.pages;
        accessoryTotal.value = data.infos.total;
        accessorySearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loadingMoreAccessories.value = false;
    });
}

function changePage(value: number) {
  page.value = value;
  fetchRelated();
}

function changeLimit(value: number) {
  limit.value = value;
  page.value = 0;
  fetchRelated();
}

function searchItem(value: string) {
  search.value = value;

  fetchRelated();
}

function resetSearchAccessory() {
  accessorySearch.value = "";
  accessorySearchList.value = [];
  accessoryPage.value = 0;
  accessoryPages.value = 0;
  accessorySearched.value = 0;
  accessoryTotal.value = 0;
  loadingMoreAccessories.value = false;
}

async function addRelated() {
  store.commit("SET_LOADING", true);

  resetSearchAccessory();
  await searchAccessories();
  store.commit("SET_LOADING", false);

  showModalAddRelated.value = true;
}

async function searchAccessory() {
  accessorySearchList.value = [];
  accessoryPage.value = 0;
  accessoryPages.value = 0;
  accessorySearched.value = 0;
  accessoryTotal.value = 0;

  await searchAccessories();
}

function onAccessoryListScroll(event: Event) {
  const target =
    (event.target as HTMLElement | null) ?? accessorySearchListRef.value;

  if (!target) {
    return;
  }

  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (
    reachedEnd &&
    !loadingMoreAccessories.value &&
    accessoryPage.value + 1 < accessoryPages.value
  ) {
    void searchAccessories(true);
  }
}

async function selectAccessory(item: AccessoryType) {
  if (!relatedIds.value.some((r) => r.accessory_id === item.accessory_id)) {
    await accessoryRelatedCreate(props.accessoryId, {
      accessory_id: item.accessory_id,
    })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  } else {
    const relation = relatedIds.value.find(
      (r) => r.accessory_id === item.accessory_id,
    );

    if (!relation) {
      return;
    }

    await accessoryRelatedDelete(relation.item_id)
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  }
}

function removeRelated(item: AccessoryRelatedType) {
  selected.value = item;
  showModalRemoveRelated.value = true;
}

function removeRelatedCancel() {
  selected.value = null;
  showModalRemoveRelated.value = false;
}

async function removeRelatedConfirm() {
  if (!selected.value) {
    return;
  }

  await accessoryRelatedDelete(selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchRelatedId();
        fetchRelated();
        showModalRemoveRelated.value = false;
        selected.value = null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => accessorySearch.value,
  () => {
    if (trim(accessorySearch.value).length === 0) {
      searchAccessory();
    }
  },
);

onMounted(async () => {
  await fetchRelated();
  await fetchRelatedId();
  loading.value = false;
});
</script>

<template>
  <section class="tab-accessory">
    <div class="tab-header">
      <svg-icon icon="gamepad" size="2rem" />
      {{ t("collection.labels.related") }}
    </div>

    <div class="tab-content">
      <template v-if="loading">
        <div class="empty-data">
          <base-loading />
        </div>
      </template>
      <template v-else>
        <base-table
          :header="tableHeader"
          :data="related"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :copy="false"
          :edit="false"
          :del="true"
          :show-limit="true"
          @change:page="changePage($event)"
          @change:limit="changeLimit($event)"
          @searching="searchItem($event)"
          @btn:add="addRelated"
          @btn:del="removeRelated($event)"
        >
          <template #avatar="{ data }">
            <base-avatar
              position="center"
              :is-edit="false"
              :show-image="true"
              :url="data?.avatar?.large"
              :aspect-ratio="1.333"
              size="3rem"
              border-radius="0.25rem"
            />
          </template>

          <template #created="{ data }">
            <span>
              {{ getDate(data.created) }} - {{ getHour(data.created) }}
            </span>
          </template>
        </base-table>
      </template>
    </div>
  </section>

  <base-confirm
    :show="showModalRemoveRelated"
    :title="t('collection.modals.titles.del_related').toUpperCase()"
    :message="
      t('collection.modals.messages.del_related_accessory', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeRelatedCancel()"
    @confirm="removeRelatedConfirm()"
  />

  <base-modal
    :show="showModalAddRelated"
    :title="t('collection.modals.titles.add_related_accessory').toUpperCase()"
    @close="showModalAddRelated = false"
  >
    <template #content>
      <div class="accessory-add">
        <div class="accessory-search">
          <base-input
            v-model="accessorySearch"
            background="#fff"
            color="#000"
            :placeholder="t('common.labels.search')"
            @key:enter="searchAccessory()"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon icon="search" size="1.75rem" @click="searchAccessory()" />
              </div>
            </template>
          </base-input>
        </div>

        <div
          ref="accessorySearchListRef"
          class="accessory-list"
          @scroll="onAccessoryListScroll"
        >
          <template v-for="(item, index) in accessorySearchList" :key="index">
            <div class="accessory">
              <base-avatar
                position="center"
                :is-edit="false"
                :show-image="true"
                :url="item?.avatar?.large"
                :aspect-ratio="1.333"
                size="3rem"
                border-radius="0.25rem"
              ></base-avatar>

              <div class="name">
                {{ item.name }}
              </div>
              <label
                class="custom-checkbox"
                :for="`related-accessory-${item.accessory_id}`"
              >
                <input
                  :id="`related-accessory-${item.accessory_id}`"
                  class="checkbox-input"
                  type="checkbox"
                  @change="selectAccessory(item)"
                  :checked="
                    relatedIds.some((r) => r.accessory_id === item.accessory_id)
                  "
                />
                <span class="checkbox-mark"></span>
              </label>
            </div>
          </template>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<style lang="scss" scoped>
.tab-accessory {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .tab-header {
    display: inline-flex;
    gap: 0.5rem;
    font-size: 1.2rem;
    align-items: center;
    padding-block-end: 0.75rem;
    text-transform: uppercase;
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .empty-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-block: 2rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }
  }
}

.accessory-add {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .accessory-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: scroll;
    max-height: 20rem;
    height: 20rem;
    padding: 0.5rem;
    background: white;
    border-radius: 0.5rem;

    .accessory {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      padding-block: 0.5rem;
      padding-inline: 1rem;
      background: #f1f1f1;
      border-radius: 0.5rem;

      .custom-checkbox {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-inline-start: auto;
        cursor: pointer;
      }

      .checkbox-input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .checkbox-mark {
        width: 1.1rem;
        height: 1.1rem;
        border: 0.125rem solid var(--primary);
        border-radius: 0.25rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        background: white;
      }

      .checkbox-input:checked + .checkbox-mark {
        background: var(--primary);
        border-color: var(--primary);
      }

      .checkbox-input:checked + .checkbox-mark::after {
        content: "";
        width: 0.3rem;
        height: 0.6rem;
        border: solid white;
        border-width: 0 0.125rem 0.125rem 0;
        transform: rotate(45deg);
        margin-top: -0.1rem;
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-accessory {
    .tab-content {
      padding-inline: 0;
    }
  }
}
</style>
